/**
 * Provides custom helpers for Algolia search integration.
 * Handles price calculations, swatches display, and facet organization
 * for a multi-store e-commerce platform.
 *
 * @author Algolia Integration Team
 * @version 1.0.0
 * @requires Algolia InstantSearch
 * @requires jQuery
 */
class algolia_custom_helpers {

  /**
   * Creates a new instance of algolia_custom_helpers.
   * Initializes default configuration and binds necessary contexts.
   *
   * @constructor
   */
  constructor() {
    // Configuration cache for performance optimization
    this.config = {
      priceCalculationDelay: 100,
      defaultPricePrecision: 2
    };
  }

  /**
   * Initializes the custom helpers.
   * Sets up event listeners and initializes the search integration.
   *
   * @returns {void}
   */
  init() {
    // Initialize any required event listeners or configurations
  }

  /**
   * Retrieves product metafields and processes prices after a short delay.
   * Used to ensure DOM elements are rendered before price updates.
   *
   * @param {Object} hit - Algolia search hit object containing product data.
   * @param {number} perPage - Number of products per page (unused, kept for compatibility).
   * @param {boolean} [autocomplete=false] - Whether this is for autocomplete suggestions.
   * @param {Function|null} [callback=null] - Optional callback to execute after price calculation.
   * @returns {void}
   * @example
   * const helpers = new algolia_custom_helpers();
   * helpers.getProductMetafieldsAndProcessPrices(hit, 20, false, () => console.log('Done'));
   */
  getProductMetafieldsAndProcessPrices(hit, perPage, autocomplete = false, callback = null) {

    let _this = this;
    setTimeout(function () {
      _this.calculateCustomMinMaxPrices(hit, autocomplete, callback);
    }, this.config.priceCalculationDelay);
  }

  /**
   * Calculates and displays custom min/max prices from product metafields.
   * Applies store-specific pricing and variant auto-discounts, then updates
   * the DOM with the calculated price range.
   *
   * @param {Object} hit - Algolia search hit object with meta property containing price data.
   * @param {boolean} [autocomplete=false] - Whether to search for autocomplete card elements.
   * @param {Function|null} [callback=null] - Optional callback to run after price update.
   * @returns {void}
   * @sideEffect Updates DOM elements with calculated price ranges and toggles CSS classes
   * @example
   * const helpers = new algolia_custom_helpers();
   * helpers.calculateCustomMinMaxPrices(hit, true);
   */
  calculateCustomMinMaxPrices(hit, autocomplete = false, callback = null) {
    // Locate the product card element in the DOM based on Algolia position
    let card = document.querySelector(`[data-algolia-position="${hit.__position}"]`);
    let target = null;
    let target2 = null;

    // Price list contains store-specific pricing data with embroidery costs included
    let priceList = null;
    if (hit.meta.hasOwnProperty('prices') &&
      hit.meta.prices.hasOwnProperty('price-lists') &&
      hit.meta.prices['price-lists'].length > 0) {
      priceList = JSON.parse(hit.meta.prices['price-lists']);
    }

    // Variant auto-discounts contain percentage-based discounts per store
    let variantAutoDiscount = null;
    if (hit.meta.hasOwnProperty('multistore') &&
      hit.meta.multistore.hasOwnProperty('variant_auto_discounts') &&
      hit.meta.multistore.variant_auto_discounts.length > 0) {
      variantAutoDiscount = JSON.parse(hit.meta.multistore.variant_auto_discounts);
    }

    // Adjust card selector for autocomplete vs regular search results
    if (autocomplete) {
      card = document.querySelector(`.aa-ItemLink[href="/products/${hit.handle}/"]`);
    }

    if (card) {
      // Primary price target for regular search results
      target = card.querySelector(`.ais-hit--price b`);

      if (autocomplete) {
        // Autocomplete has separate price elements - custom and default
        target = card.querySelector('.aa-ItemContentPrice.custom-price .aa-ItemContentPriceCurrent');
        target2 = card.querySelector('.aa-ItemContentPrice:not(.custom-price) .aa-ItemContentPriceCurrent');
      }
    }

    // Find parent containers for price elements to toggle visibility
    let parent1, parent2 = null;
    if (target) {
      parent1 = target.closest('.aa-ItemContentPrice');
      if (!parent1) parent1 = target.closest('.ais-hit--price');
    }
    if (target2) {
      parent2 = target2.closest('.aa-ItemContentPrice');
      if (!parent2) parent2 = target2.closest('.ais-hit--price');
    }

    // Fallback to default price if store context is not available
    if (!window.hasOwnProperty('multistore_id')) {
      if (parent2) parent2.classList.remove('active');
      else if (parent1) parent1.classList.add('active');
      return;
    }

    // Show default price when custom price list is unavailable
    if (!priceList) {
      if (parent2) {
        parent2.classList.add('active');
        if (parent1) parent1.classList.remove('active');
      } else if (parent1) parent1.classList.add('active');
    }

    let minPrice = null;
    let maxPrice = null;
    let storeString = 'store_' + window.multistore_id;
    let discountCalculated = false;

    // Process price list from metafields - iterate through all variants
    if (priceList) {
      for (const [variantId, prices] of Object.entries(priceList)) {

        let discount = 0;

        // Retrieve variant-specific auto-discount for current store
        if (variantAutoDiscount && variantAutoDiscount.hasOwnProperty(variantId)) {
          if (typeof variantAutoDiscount[variantId] != "undefined" && variantAutoDiscount[variantId].length > 0) {
            let variantDiscounts = JSON.parse(variantAutoDiscount[variantId]);
            if (variantDiscounts) {
              variantDiscounts.some((discountData) => {
                let splitDiscount = discountData.split(':');
                let storeId = splitDiscount[0];
                if (parseInt(storeId) == window.multistore_id) {
                  let discountValue = parseInt(splitDiscount[1]);
                  if (!isNaN(discountValue)) {
                    discount = discountValue;
                    discountCalculated = true;
                  }
                  return true;
                }
              });
            }
          }
        }

        // Apply discount as percentage multiplier
        discount = (100 - discount) / 100;

        // Extract price for current store from variant prices
        if (prices.hasOwnProperty(storeString) || prices.hasOwnProperty(window.multistore_id)) {
          let price = prices.hasOwnProperty(storeString) ? prices[storeString] : prices[window.multistore_id];

          // Handle wizard-based pricing (customer-specific configuration)
          if (typeof price == 'object') {
            if (window.hasOwnProperty('wizard_answers') && price.hasOwnProperty(window.wizard_answers)) {
              price = price[window.wizard_answers];
            }
          }

          // Update min/max price range with calculated values
          if (price < minPrice || !minPrice) minPrice = (price * discount).toFixed(this.config.defaultPricePrecision);
          if (price > maxPrice || !maxPrice) maxPrice = (price * discount).toFixed(this.config.defaultPricePrecision);
          discountCalculated = true;
        } else if (prices.hasOwnProperty(hit.objectID)) {
          // Fallback to objectID-based price lookup
          if (prices[hit.objectID].hasOwnProperty(storeString) || prices[hit.objectID].hasOwnProperty(window.multistore_id)) {
            let price = prices[hit.objectID].hasOwnProperty(storeString) ? prices[storeString] : prices[hit.objectID][window.multistore_id];
            if (price < minPrice || !minPrice) minPrice = (price * discount).toFixed(this.config.defaultPricePrecision);
            if (price > maxPrice || !maxPrice) maxPrice = (price * discount).toFixed(this.config.defaultPricePrecision);
            discountCalculated = true;
          }
        }
      }
    }

    // Parse variant prices from stores metafield (fallback when no price-lists metafield)
    let variantPrices = null;
    if (hit.meta.hasOwnProperty('stores') &&
      hit.meta.stores.hasOwnProperty('variant_prices') &&
      hit.meta.stores.variant_prices.length > 0) {
      variantPrices = hit.meta.stores.variant_prices.split(',');
    }

    // Process variant prices when product-level price list is unavailable
    if (!discountCalculated && variantPrices && variantPrices.length > 0) {

      variantPrices.forEach((variantData) => {
        variantData = variantData.split(':');
        let variantId = variantData[0];
        let price = parseFloat(variantData[1]);
        let discount = 0

        // Apply variant auto-discounts to fallback prices
        if (variantAutoDiscount && variantAutoDiscount.hasOwnProperty(variantId)) {
          if (typeof variantAutoDiscount[variantId] != "undefined" && variantAutoDiscount[variantId].length > 0) {
            let variantDiscounts = JSON.parse(variantAutoDiscount[variantId]);
            if (variantDiscounts) {
              variantDiscounts.some((discountData) => {
                let splitDiscount = discountData.split(':');
                let storeId = splitDiscount[0];
                if (parseInt(storeId) == window.multistore_id) {
                  let discountValue = parseInt(splitDiscount[1]);
                  if (!isNaN(discountValue)) {
                    discount = discountValue;
                  }
                  return true;
                }
              });
            }
          }
        }

        discount = (100 - discount) / 100;

        if (price < minPrice || !minPrice) minPrice = (price * discount).toFixed(this.config.defaultPricePrecision);
        if (price > maxPrice || !maxPrice) maxPrice = (price * discount).toFixed(this.config.defaultPricePrecision);

      });
    }

    // Ensure min/max are properly ordered (min should be less than max)
    if (maxPrice < minPrice) {
      let _minPrice = minPrice;
      minPrice = maxPrice;
      maxPrice = _minPrice;
    }

    // Normalize price values to proper decimal format
    if (minPrice) {
      minPrice = parseFloat(minPrice).toFixed(this.config.defaultPricePrecision);
    }
    if (minPrice) {
      maxPrice = parseFloat(maxPrice).toFixed(this.config.defaultPricePrecision);
    }

    // Format min/max price string for display
    let minMax = null;
    if (minPrice && maxPrice && minPrice != maxPrice) {
      minMax = `$${minPrice} - $${maxPrice}`;
    } else if (minPrice && !maxPrice) {
      minMax = '$' + minPrice;
    } else if (!minPrice && maxPrice) {
      minMax = '$' + maxPrice;
    } else if (minPrice == maxPrice) {
      minMax = '$' + maxPrice;
    }

    // Execute callback if provided
    if (typeof callback == 'function') callback();

    // Update DOM with calculated price range
    if (minMax) {
      if (card) {
        if (target) {
          target.innerText = minMax;
          if (parent1) {
            parent1.classList.add('active');

            if (parent2) {
              parent2.classList.remove('active');
            }
          }
        }
      }
    } else if (parent2) {
      // Show default price when custom pricing unavailable
      parent2.classList.add('active');
      if (parent1) {
        parent1.classList.remove('active');
      }
    } else if (parent1) {
      parent1.classList.add('active');
      if (parent2) {
        parent2.classList.remove('active');
      }
    }
  }

  /**
   * Updates the swatches count displayed on a product card based on metadata.
   * Parses store-specific swatch counters and applies wizard-based filtering.
   *
   * @param {Object} hit - The product hit object containing metadata about the swatches count.
   * @param {boolean} [autocomplete=false] - A flag indicating whether to search for cards in autocomplete suggestions.
   * @returns {void}
   * @sideEffect Modifies DOM element text content and toggles visibility of swatch count badge
   * @example
   * const helpers = new algolia_custom_helpers();
   * helpers.addSwatchesCountToCard(hit, true);
   */
  addSwatchesCountToCard(hit, autocomplete = false) {
    // Validate hit object and swatches counter existence
    if (!hit || !hit.meta?.swatches?.hasOwnProperty('counters')) return;

    let storeString = `store_${window.multistore_id}`;
    let count = 0;

    // Parse swatch counters from JSON metadata
    let json = JSON.parse(hit.meta.swatches.counters);

    // Get default (non-wizard) count for current store
    if (json[storeString]?.hasOwnProperty('non_wizard')) {
      count = json[storeString]?.non_wizard;
    }

    // Override with wizard-specific count if customer has answered wizard questions
    if (window.hasOwnProperty('wizard_answers')) {
      if(json[storeString]?.hasOwnProperty(window.wizard_answers)) {
        count = json[storeString][window.wizard_answers];
      }
    }

    // Skip update if no swatches available for this store/configuration
    if(count < 1) return;

    // Locate the product card element
    let card = document.querySelector(`[data-algolia-position="${hit.__position}"]`);

    if (card) {
      // Adjust selector for autocomplete mode
      if (autocomplete) {
        card = document.querySelector(`.aa-ItemLink[href="/products/${hit.handle}/"]`);
      }

      // Find and update swatches count element
      let target = card.querySelector('.ais-hit--swatches-count');
      if(target) {
        // Replace placeholder with actual count value
        target.textContent = target.textContent.replace('[[VALUE]]', count);

        // Handle singular/plural grammar
        if (count == 1) {
          target.textContent = target.textContent.replace('colors', 'color');
        }

        // Reveal the swatches count badge
        target.classList.remove('hidden');
      }
    }
  }

  /**
   * Organizes collection facets by moving subcollection items under their
   * parent collections in the DOM. Uses data-subcategories to determine hierarchy.
   *
   * @returns {void}
   * @sideEffect Manipulates DOM structure by moving subcategory elements under parent categories
   * @example
   * const helpers = new algolia_custom_helpers();
   * helpers.organizeCollectionFacets();
   */
  organizeCollectionFacets() {
    // Locate facet containers in the DOM
    let facetsContainer = document.querySelector('.ais-facets');
    let collectionsContainer = document.querySelector('.ais-facet-custom-collections');
    let subcategories = facetsContainer.dataset.subcategories;

    // Exit early if no subcategories defined
    if (subcategories.length < 1) return;

    subcategories = JSON.parse(subcategories);
    let collections = collectionsContainer.querySelectorAll('.ais-facet--item');

    // Process each subcollection and nest under parent
    subcategories.forEach(function (collection) {
      let handle = collection.handle;
      let parentTarget = document.querySelector(`.ais-facet--item[data-handle="${handle}"]`);
      if (!parentTarget) return;

      // Get the subcategories container within parent
      parentTarget = parentTarget.parentNode.querySelector('.subcategories');
      if (parentTarget && collection.hasOwnProperty('subcollections')) {
        collection.subcollections.forEach(function (subcollection) {
          let target = document.querySelector(`.ais-facet--item[data-handle="${subcollection.handle}"]`);
          if (!target) return;

          // Move subcollection element under parent in DOM
          target = target.parentNode;
          if (target) {
            parentTarget.appendChild(target);
            parentTarget.closest('.ais-facet-collections-item').classList.toggle('has-subcategories', true);
          }
        });
      }
    });
  }
}
